/**
 * Session Memory Contract
 *
 * Issue #261: AI-Powered Data Chat Enhancement - Phase 4 (Session Management)
 *
 * Defines interfaces for Mem0-style persistent session memory:
 * - Automatic memory extraction from conversations
 * - User preferences and facts storage
 * - Cross-session memory retrieval
 * - Memory scoring and importance
 *
 * @see https://mem0.ai/docs
 */

import { z } from 'zod';

// ============================================================================
// ENUMS
// ============================================================================

export enum MemoryType {
  FACT = 'FACT', // Factual information about user/context
  PREFERENCE = 'PREFERENCE', // User preferences
  INSTRUCTION = 'INSTRUCTION', // User instructions/rules
  CONTEXT = 'CONTEXT', // Contextual information
  FEEDBACK = 'FEEDBACK', // User feedback
  ENTITY = 'ENTITY', // Named entities
}

export enum MemorySource {
  EXTRACTED = 'EXTRACTED', // Auto-extracted from conversation
  EXPLICIT = 'EXPLICIT', // User explicitly stated
  INFERRED = 'INFERRED', // Inferred from behavior
  SYSTEM = 'SYSTEM', // System-generated
}

export enum MemoryStatus {
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
  DELETED = 'DELETED',
  SUPERSEDED = 'SUPERSEDED', // Replaced by newer memory
}

// ============================================================================
// CORE TYPES
// ============================================================================

/**
 * A single memory entry
 */
export interface Memory {
  id: string;
  businessId: string;
  userId: string;
  sessionId?: string;
  type: MemoryType;
  source: MemorySource;
  status: MemoryStatus;
  content: string;
  metadata?: {
    category?: string;
    tags?: string[];
    confidence?: number;
    extractedFrom?: string;
    entities?: string[];
    supersededBy?: string;
    [key: string]: unknown;
  };
  importance: number; // 0.0 - 1.0
  accessCount: number;
  lastAccessedAt: Date;
  createdAt: Date;
  updatedAt: Date;
  expiresAt?: Date;
}

/**
 * Memory search result
 */
export interface MemorySearchResult {
  memory: Memory;
  relevanceScore: number;
  matchedOn: string[]; // What fields matched
}

/**
 * Extracted memory from conversation
 */
export interface ExtractedMemory {
  content: string;
  type: MemoryType;
  source: MemorySource;
  confidence: number;
  metadata?: Record<string, unknown>;
}

// ============================================================================
// REQUEST/RESPONSE TYPES
// ============================================================================

/**
 * Add memory request
 */
export interface AddMemoryRequest {
  businessId: string;
  userId: string;
  sessionId?: string;
  content: string;
  type: MemoryType;
  source?: MemorySource;
  metadata?: Record<string, unknown>;
  importance?: number;
  expiresAt?: Date;
}

/**
 * Add memory response
 */
export interface AddMemoryResponse {
  memoryId: string;
  createdAt: Date;
}

/**
 * Search memories request
 */
export interface SearchMemoriesRequest {
  businessId: string;
  userId: string;
  query?: string;
  types?: MemoryType[];
  minImportance?: number;
  limit?: number;
  includeArchived?: boolean;
}

/**
 * Search memories response
 */
export interface SearchMemoriesResponse {
  memories: MemorySearchResult[];
  total: number;
  searchDuration: number;
}

/**
 * Get user memories request
 */
export interface GetUserMemoriesRequest {
  businessId: string;
  userId: string;
  sessionId?: string;
  types?: MemoryType[];
  limit?: number;
  offset?: number;
}

/**
 * Get user memories response
 */
export interface GetUserMemoriesResponse {
  memories: Memory[];
  total: number;
  hasMore: boolean;
}

/**
 * Update memory request
 */
export interface UpdateMemoryRequest {
  businessId: string;
  memoryId: string;
  content?: string;
  importance?: number;
  metadata?: Record<string, unknown>;
  status?: MemoryStatus;
}

/**
 * Update memory response
 */
export interface UpdateMemoryResponse {
  memory: Memory;
}

/**
 * Delete memory request
 */
export interface DeleteMemoryRequest {
  businessId: string;
  memoryId: string;
  hard?: boolean; // Hard delete vs soft delete
}

/**
 * Delete memory response
 */
export interface DeleteMemoryResponse {
  success: boolean;
  deletedAt: Date;
}

/**
 * Extract memories from conversation request
 */
export interface ExtractMemoriesRequest {
  businessId: string;
  userId: string;
  sessionId: string;
  messages: Array<{
    role: 'user' | 'assistant' | 'system';
    content: string;
  }>;
  existingMemories?: Memory[];
}

/**
 * Extract memories from conversation response
 */
export interface ExtractMemoriesResponse {
  extracted: ExtractedMemory[];
  saved: AddMemoryResponse[];
  superseded: string[]; // IDs of memories that were superseded
  extractionDuration: number;
}

/**
 * Get session context request
 */
export interface GetSessionContextRequest {
  businessId: string;
  userId: string;
  sessionId: string;
  maxMemories?: number;
  relevanceThreshold?: number;
}

/**
 * Get session context response - optimized for LLM context
 */
export interface GetSessionContextResponse {
  systemPromptAddition: string; // Formatted for system prompt
  memories: Memory[];
  totalRelevantMemories: number;
}

// ============================================================================
// ZOD SCHEMAS
// ============================================================================

export const memoryTypeSchema = z.nativeEnum(MemoryType);
export const memorySourceSchema = z.nativeEnum(MemorySource);
export const memoryStatusSchema = z.nativeEnum(MemoryStatus);

export const addMemoryRequestSchema = z.object({
  businessId: z.string().min(1, 'Business ID is required'),
  userId: z.string().min(1, 'User ID is required'),
  sessionId: z.string().optional(),
  content: z.string().min(1, 'Memory content is required'),
  type: memoryTypeSchema,
  source: memorySourceSchema.optional().default(MemorySource.EXPLICIT),
  metadata: z.record(z.string(), z.unknown()).optional(),
  importance: z.number().min(0).max(1).optional().default(0.5),
  expiresAt: z.date().optional(),
});

export const searchMemoriesRequestSchema = z.object({
  businessId: z.string().min(1, 'Business ID is required'),
  userId: z.string().min(1, 'User ID is required'),
  query: z.string().optional(),
  types: z.array(memoryTypeSchema).optional(),
  minImportance: z.number().min(0).max(1).optional(),
  limit: z.number().min(1).max(100).optional().default(20),
  includeArchived: z.boolean().optional().default(false),
});

export const getUserMemoriesRequestSchema = z.object({
  businessId: z.string().min(1, 'Business ID is required'),
  userId: z.string().min(1, 'User ID is required'),
  sessionId: z.string().optional(),
  types: z.array(memoryTypeSchema).optional(),
  limit: z.number().min(1).max(100).optional().default(50),
  offset: z.number().min(0).optional().default(0),
});

export const updateMemoryRequestSchema = z.object({
  businessId: z.string().min(1, 'Business ID is required'),
  memoryId: z.string().min(1, 'Memory ID is required'),
  content: z.string().optional(),
  importance: z.number().min(0).max(1).optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  status: memoryStatusSchema.optional(),
});

export const deleteMemoryRequestSchema = z.object({
  businessId: z.string().min(1, 'Business ID is required'),
  memoryId: z.string().min(1, 'Memory ID is required'),
  hard: z.boolean().optional().default(false),
});

export const extractMemoriesRequestSchema = z.object({
  businessId: z.string().min(1, 'Business ID is required'),
  userId: z.string().min(1, 'User ID is required'),
  sessionId: z.string().min(1, 'Session ID is required'),
  messages: z.array(
    z.object({
      role: z.enum(['user', 'assistant', 'system']),
      content: z.string(),
    }),
  ),
});

export const getSessionContextRequestSchema = z.object({
  businessId: z.string().min(1, 'Business ID is required'),
  userId: z.string().min(1, 'User ID is required'),
  sessionId: z.string().min(1, 'Session ID is required'),
  maxMemories: z.number().min(1).max(50).optional().default(10),
  relevanceThreshold: z.number().min(0).max(1).optional().default(0.5),
});

// ============================================================================
// DEFAULT CONFIGURATION
// ============================================================================

export interface SessionMemoryConfig {
  enabled: boolean;
  autoExtract: boolean;
  maxMemoriesPerUser: number;
  defaultExpiration: number; // Days
  minConfidenceForSave: number;
  maxSessionContextMemories: number;
}

export const DEFAULT_SESSION_MEMORY_CONFIG: SessionMemoryConfig = {
  enabled: true,
  autoExtract: true,
  maxMemoriesPerUser: 1000,
  defaultExpiration: 365, // 1 year
  minConfidenceForSave: 0.7,
  maxSessionContextMemories: 10,
};
